import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Link, router, usePage } from "@inertiajs/react";
import { ComponentProps } from "react";
import { LayoutDashboardIcon, LogOutIcon, LucideIcon, StoreIcon, UsersIcon } from "lucide-react";
import useTranslations from "@/hooks/use-translations";
import {
  superadmin_dashboard_path,
  superadmin_stores_path,
  superadmin_users_path,
} from "@/lib/routes";
import { SharedProps } from "@/types";

function useSidebarNavMain(): {
  title: string;
  url: string;
  icon: LucideIcon;
}[] {
  const { t } = useTranslations("shared.components.superadmin_sidebar");

  return [
    { title: t("dashboard"), url: superadmin_dashboard_path(), icon: LayoutDashboardIcon },
    { title: t("users"), url: superadmin_users_path(), icon: UsersIcon },
    { title: t("stores"), url: superadmin_stores_path(), icon: StoreIcon },
  ];
}

function isLinkActive(url: string): boolean {
  const { url: currentUrl } = usePage();

  return currentUrl === url;
}

export default function SuperadminSidebar(props: ComponentProps<typeof Sidebar>) {
  const { current_user } = usePage<SharedProps>().props;
  const { t } = useTranslations("shared.components.superadmin_sidebar");
  const sidebarNavMain = useSidebarNavMain();

  return (
    <Sidebar {...props}>
      <SidebarHeader>
        <div className="px-2 py-1.5 text-sm font-semibold">
          {t("title")}
        </div>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarMenu>
            {sidebarNavMain.map((item) => (
              <SidebarMenuItem key={item.title}>
                <SidebarMenuButton asChild isActive={isLinkActive(item.url)}>
                  <Link href={item.url}>
                    <item.icon />
                    <span>{item.title}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        {current_user && (
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton size="lg">
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarFallback className="rounded-lg">
                    {current_user.email[0].toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">
                    {current_user.email}
                  </span>
                </div>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton onClick={() => router.delete("/session")}>
                <LogOutIcon />
                {t("log_out")}
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}
